import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "../loader/loader";
import ToastrService from "../../services/toastService";
import Swal from "sweetalert2";
import { useGetCampaignAgentsMutation, useGetCampaignMutation, useRemoveAgentMutation } from './Api';
import AddAgentModal from './AddAgentModal';
import { FiEye, FiChevronUp, FiChevronDown, FiUsers, FiUserCheck } from "react-icons/fi";
import { BsMegaphone } from "react-icons/bs";
import { MdOutlineAccountTree } from "react-icons/md";

const GRID_COLUMNS = "40px minmax(180px, 2fr) 1fr 1fr 90px";

const AgentAssignment = () => {

    const navigate = useNavigate();
    const location = useLocation();
    const campaignIdFromList = location.state?.campaignId;

    const [campaigns, setCampaigns] = useState([]);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [campaignData, setCampaignData] = useState(null);

    const [selectedAgentIds, setSelectedAgentIds] = useState([]);
    const [selectedSupervisorId, setSelectedSupervisorId] = useState(null);
    const [search, setSearch] = useState({});
    const [debouncedSearch, setDebouncedSearch] = useState({});

    const [showAddAgentModal, setShowAddAgentModal] = useState(false);

    const [queues, setQueues] = useState([]);
    const [openQueueId, setOpenQueueId] = useState(null);
    const [isFirstLoad, setIsFirstLoad] = useState(!!campaignIdFromList);
    const [searchingQueueId, setSearchingQueueId] = useState(null);

    const [getCampaign, { isLoading: campaignLoading }] = useGetCampaignMutation();
    const [getCampaignAgents] = useGetCampaignAgentsMutation();
    const [removeAgent] = useRemoveAgentMutation();

    const searchTimers = useRef({});
    const lastSearchQueueId = useRef(null);

    useEffect(() => {
        fetchCampaign();
    }, []);

    useEffect(() => {
        if (!selectedCampaign) {
            setQueues([]);
            setSelectedAgentIds([]);
            setOpenQueueId(null);
            return;
        }
        setIsFirstLoad(true);
        setOpenQueueId(null);
        setSearch({});
        setDebouncedSearch({});
        setSelectedAgentIds([]);

        // Clear search timers
        Object.values(searchTimers.current).forEach((timer) => {
            clearTimeout(timer);
        });

        searchTimers.current = {};
        lastSearchQueueId.current = null;

        fetchAgents(selectedCampaign, null, "", false, true);
    }, [selectedCampaign]);


    useEffect(() => {
        const queueId = lastSearchQueueId.current;
        if (!selectedCampaign || queueId === null) {
            return;
        }
        const searchValue = debouncedSearch[queueId] || "";
        fetchAgents(selectedCampaign, queueId, searchValue, true, false);
    }, [debouncedSearch]);


    const handleAgentSearch = (queueId, value) => {
        setSearch((prev) => ({
            ...prev,
            [queueId]: value,
        }));

        Object.keys(searchTimers.current).forEach((id) => {
            if (Number(id) !== Number(queueId)) {
                clearTimeout(searchTimers.current[id]);
                delete searchTimers.current[id];
            }
        });

        if (searchTimers.current[queueId]) {
            clearTimeout(searchTimers.current[queueId]);
        }

        searchTimers.current[queueId] = setTimeout(() => {
            lastSearchQueueId.current = queueId;

            setDebouncedSearch((prev) => ({
                ...prev,
                [queueId]: value.trim(),
            }));
        }, 500);
    };


    const fetchCampaign = async () => {
        try {
            const payload = campaignIdFromList ? { id: campaignIdFromList } : {};

            const res = await getCampaign(payload).unwrap();

            const campaignList = res?.data || [];
            setCampaigns(campaignList);

            if (campaignIdFromList) {
                const selected = campaignList.find((campaign) => campaign.id === campaignIdFromList);
                setSelectedCampaign(campaignIdFromList);
                setCampaignData(selected || null);
            } else {
                setSelectedCampaign(null);
                setCampaignData(null);
            }

        } catch (error) {
            console.error("Error fetching campaign:", error);
            setCampaigns([]);
            setSelectedCampaign(null);
            setCampaignData(null);
        }
    };


    const fetchAgents = async (campaignId, queueId = null, searchValue = "", isSearch = false, isCampaignLoad = false) => {
        try {
            if (isSearch) {
                setSearchingQueueId(queueId);
            }
            const payload = {
                id: campaignId,
                queueId: queueId,
                search: searchValue.trim(),
            };

            const res = await getCampaignAgents(payload).unwrap();
            const queueData = res?.data?.data || [];
            if (!queueId) {
                setQueues(queueData);
                if (queueData.length > 0) {
                    setOpenQueueId(queueData[0].id);
                }
                return;
            }
            const updatedQueue = queueData.find((queue) => Number(queue.id) === Number(queueId));

            if (!updatedQueue) {
                console.warn("Queue not found in API response:", queueId);
                return;
            }

            setQueues((prevQueues) =>
                prevQueues.map((queue) =>
                    Number(queue.id) === Number(queueId)
                        ? updatedQueue
                        : queue
                )
            );
            setOpenQueueId(queueId);
        } catch (error) {
            console.error("Error fetching agents:", error);
            if (isCampaignLoad) {
                setQueues([]);
                setOpenQueueId(null);
            }
        } finally {
            if (isSearch) {
                setSearchingQueueId(null);
            }

            // First agent API call completed
            if (isCampaignLoad) {
                setIsFirstLoad(false);
            }
        }
    };

    const handleQueueToggle = (queueId) => {
        const isCurrentlyOpen = openQueueId === queueId;

        if (isCurrentlyOpen) {
            setOpenQueueId(null);
            setSelectedAgentIds([]);
            return;
        }

        setOpenQueueId(queueId);
        setSelectedAgentIds([]);

        // Clear search for the newly opened queue
        setSearch((prev) => ({
            ...prev,
            [queueId]: "",
        }));

        setDebouncedSearch((prev) => ({
            ...prev,
            [queueId]: "",
        }));

        // Cancel pending searches
        Object.values(searchTimers.current).forEach((timer) => {
            clearTimeout(timer);
        });

        searchTimers.current = {};
        lastSearchQueueId.current = null;

        // Fetch all agents for the newly opened queue
        fetchAgents(selectedCampaign, queueId, "", false, false);
    };

    const handleCampaignSelect = async (campaignId) => {
        const selected = campaigns.find((campaign) => campaign.id === campaignId);
        setSelectedCampaign(campaignId);
        setCampaignData(selected || null);
    };

    const handleAgentSelect = (agentId, supervisorId) => {
        if (selectedSupervisorId !== supervisorId) {
            setSelectedSupervisorId(supervisorId);
            setSelectedAgentIds([agentId]);
            return;
        }
        setSelectedAgentIds((prev) =>
            prev.includes(agentId)
                ? prev.filter((id) => id !== agentId)
                : [...prev, agentId]
        );
    };

    const handleViewCampaign = (campaignId) => {
        navigate("/campaign/reporting-hierarchy", {
            state: {
                campaignId: campaignId,
            },
        });
    };


    const refreshCampaignData = async (campaignId) => {
        try {
            const res = await getCampaign({ id: campaignId }).unwrap();
            const campaignList = res?.data || [];
            setCampaigns(campaignList);
            const updatedCampaign = campaignList.find((campaign) => campaign.id === campaignId); setCampaignData(updatedCampaign || null);
        } catch (error) {
            console.error("Error refreshing campaign data:", error);
        }
    };

    const handleRemove = async (campaignId, queueId, agentIds = [], supervisorId = null) => {
        if (agentIds.length === 0 && !supervisorId) return;

        const isAgentRemove = agentIds.length > 0;

        const removeText = isAgentRemove
            ? `${agentIds.length > 1 ? "these agents" : "this agent"}`
            : "this supervisor";


        const result = await Swal.fire({
            icon: "warning",
            title: "Are you sure?",
            text: `Are you sure you want to remove ${removeText}?`,
            showCancelButton: true,
            confirmButtonText: "Yes, Remove",
            cancelButtonText: "Cancel",
            allowOutsideClick: false,
            allowEscapeKey: false,
        });

        // User clicked Cancel
        if (!result.isConfirmed) {
            return;
        }

        const payload = {
            campaignId,
            queueId,
            ...(agentIds.length > 0 && { agentIds }),
            ...(supervisorId && { supervisorId }),
        };

        try {
            const res = await removeAgent(payload).unwrap();
            if (res?.success) {
                ToastrService.success(res?.message || "Agents removed successfully!");
                if (agentIds.length > 0) {
                    setSelectedAgentIds((prev) =>
                        prev.filter((id) => !agentIds.includes(id))
                    );
                    setSelectedSupervisorId(null);
                }
                await fetchAgents(campaignId, queueId);
                await refreshCampaignData(campaignId);
            } else {
                ToastrService.error(res?.message || "Failed to remove agents");
            }
        } catch (error) {
            console.error("Error removing agents:", error);
        }
    };

    return (

        <div>
            {campaignLoading || isFirstLoad ? <Loader /> :
                <>
                    {/* Header */}
                    <div className="page-header">
                        <div className="page-header-left d-flex align-items-center">
                            <ul className="breadcrumb mb-0">
                                <li className="breadcrumb-item">Campaigns</li>
                                <li className="breadcrumb-item active">Agent Assignment</li>
                            </ul>
                        </div>
                    </div>

                    <div className="main-content">
                        <div className="row mb-3">
                            <div className="col-lg-8 col-md-7">
                                <h3 className="h4 mb-1 drawer-header">
                                    Agent Assignment
                                </h3>

                                <p className="mb-0 text-muted">
                                    Assign agents to campaigns and configure queue membership
                                </p>
                            </div>

                            {/* <div className="col-lg-4 col-md-5 mt-3 mt-md-0">
                                <div className="d-flex justify-content-md-end">
                                    <button className="btn btn-primary px-3 py-2" >
                                        Save Assignment
                                    </button>
                                </div>
                            </div> */}
                        </div>

                        <div className="row g-3 mb-3 align-items-stretch">
                            <div className="col-12 col-xxl-4 d-flex">
                                <div className="card stretch stretch-full overflow-hidden flex-fill h-100">
                                    <div className="bg-white border-bottom px-4 py-3">
                                        <div className="d-flex flex-column">
                                            <h5 className="mb-1 fw-semibold text-dark">Select Campaign</h5>
                                            <span className="text-muted">
                                                Choose a campaign to manage assignments
                                            </span>
                                        </div>
                                    </div>

                                    <div className="card-body pt-2 d-flex flex-column gap-2 flex-grow-1"
                                        style={{
                                            minHeight: 0,
                                            overflowY: "auto",
                                        }}>
                                        {campaigns && campaigns.length > 0 ? (
                                            campaigns.map((campaign) => {
                                                const isSelected = selectedCampaign === campaign.id;

                                                return (
                                                    <div
                                                        key={campaign.id}
                                                        onClick={() => handleCampaignSelect(campaign.id)}
                                                        className={`w-100 text-start rounded-3 p-2 ${isSelected
                                                            ? "border border-primary bg-soft-primary"
                                                            : "border bg-white"
                                                            }`}
                                                        style={{
                                                            cursor: "pointer",
                                                            transition: "all 0.2s ease",
                                                            boxShadow: isSelected
                                                                ? "0 0 0 1px rgba(13, 110, 253, 0.15)"
                                                                : "none",
                                                        }}
                                                    >
                                                        <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-2">

                                                            {/* Campaign Details */}
                                                            <div className="d-flex flex-column">
                                                                <span className="fw-semibold text-dark">
                                                                    {campaign.campaign_name}
                                                                </span>

                                                                <span
                                                                    className={
                                                                        isSelected
                                                                            ? "text-primary small"
                                                                            : "text-muted small"
                                                                    }
                                                                >
                                                                    {campaign.agents_assigned} agents assigned
                                                                </span>
                                                            </div>

                                                            <div className="d-flex align-items-center gap-2">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-light border px-2 py-1 d-flex align-items-center gap-1"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleViewCampaign(campaign.id);
                                                                    }}
                                                                    title="View Campaign"
                                                                >
                                                                    <FiEye size={15} />View
                                                                </button>

                                                                {/* Campaign Type */}
                                                                <span
                                                                    className={`badge rounded-pill fw-medium px-2 py-1 ${campaign.campaign_type.toLowerCase() === "outbound"
                                                                        ? "text-indigo bg-soft-indigo"
                                                                        : "text-success bg-soft-success"
                                                                        }`}
                                                                    style={{
                                                                        minWidth: "80px",
                                                                        textAlign: "center",
                                                                    }}
                                                                >
                                                                    {campaign.campaign_type}
                                                                </span>
                                                            </div>

                                                        </div>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div className="d-flex flex-column align-items-center justify-content-center text-center flex-grow-1">
                                                <BsMegaphone className="text-muted fs-3 mb-2" />

                                                <span className="fw-semibold text-dark">
                                                    No campaigns available
                                                </span>

                                                <span className="text-muted small mt-1">
                                                    There are no campaigns to display.
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="col-12 col-xxl-8 d-flex">
                                <div className="card stretch stretch-full overflow-hidden flex-fill h-100">
                                    {campaignData?.campaign_name && (
                                        <div className="bg-white border-bottom px-4 py-3">
                                            <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3">
                                                <div className="d-flex flex-column">
                                                    <h5 className="mb-1 fw-semibold text-dark">
                                                        {campaignData.campaign_name} - Queue Assignment
                                                    </h5>
                                                    <span className="text-muted">
                                                        Manage queue-wise supervisors and agents
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div className="card-body p-0 d-flex flex-column flex-grow-1"
                                        style={{
                                            minHeight: 0,
                                            overflowY: "auto",
                                        }}
                                    >
                                        {campaignData ? (

                                            queues.length > 0 ? (

                                                <div className="p-3">

                                                    {queues.map((queue) => {

                                                        const isOpen = openQueueId === queue.id;

                                                        const totalAgents = queue.supervisors?.reduce((total, supervisor) => total + (supervisor.agents?.length || 0), 0) || 0;

                                                        return (
                                                            <div
                                                                key={queue.id}
                                                                className="border rounded-3 overflow-hidden mb-2"
                                                            >
                                                                <div
                                                                    className="px-3 py-2 bg-gray-50 d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-2"
                                                                    style={{ cursor: "pointer" }}
                                                                    onClick={() => handleQueueToggle(queue.id)}
                                                                >

                                                                    {/* Queue Name */}
                                                                    <div className="fw-semibold text-dark">
                                                                        {queue.queue_name}
                                                                    </div>


                                                                    <div className="d-flex align-items-center gap-2">
                                                                        {isOpen && (
                                                                            <>

                                                                                <div className="position-relative flex-grow-1">
                                                                                    <input
                                                                                        type="text"
                                                                                        className="form-control form-control-sm search-agent-assign"
                                                                                        placeholder="Search agents..."
                                                                                        value={search[queue.id] || ""}
                                                                                        onClick={(e) => e.stopPropagation()}
                                                                                        onKeyDown={(e) => e.stopPropagation()}
                                                                                        onChange={(e) => {
                                                                                            handleAgentSearch(queue.id, e.target.value);
                                                                                        }}
                                                                                    />
                                                                                </div>

                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-sm btn-primary fw-semibold text-nowrap"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        setShowAddAgentModal(true);
                                                                                    }}
                                                                                >
                                                                                    + Add Agent
                                                                                </button>


                                                                                {/* Agent Count */}
                                                                                <span className="badge bg-soft-primary text-primary rounded-pill">
                                                                                    {totalAgents}
                                                                                </span>
                                                                            </>
                                                                        )}

                                                                        {/* Arrow */}
                                                                        <span className="flex-shrink-0">
                                                                            {isOpen ? <FiChevronUp /> : <FiChevronDown />}
                                                                        </span>

                                                                    </div>
                                                                </div>

                                                                {isOpen && (

                                                                    <div className="p-2"
                                                                        style={{
                                                                            maxHeight: "400px",
                                                                            overflowY: "auto",
                                                                            overflowX: "hidden",
                                                                        }}>
                                                                        {searchingQueueId === queue.id ? (

                                                                            // SEARCH LOADER
                                                                            <div
                                                                                className="d-flex flex-column align-items-center justify-content-center text-center py-5"
                                                                                style={{ minHeight: "180px" }}
                                                                            >
                                                                                <div
                                                                                    className="spinner-border spinner-border-sm text-primary mb-2"
                                                                                    role="status"
                                                                                />

                                                                                <span className="fw-semibold text-dark">
                                                                                    Searching agents...
                                                                                </span>

                                                                                <span className="text-muted small mt-1">
                                                                                    Please wait while we search for agents.
                                                                                </span>
                                                                            </div>

                                                                        ) : (


                                                                            <>
                                                                                {queue.supervisors?.length > 0 ? (

                                                                                    queue.supervisors.map((supervisor, index) => (

                                                                                        <div
                                                                                            key={supervisor.id}
                                                                                            className="border rounded-3 overflow-hidden mb-2"
                                                                                        >
                                                                                            <div className="px-3 py-2 bg-white border-bottom">
                                                                                                <div className="d-flex align-items-center justify-content-between">

                                                                                                    <div className="d-flex align-items-center gap-3">
                                                                                                        <div className="d-flex flex-column">

                                                                                                            <span className="fw-semibold text-dark">
                                                                                                                Supervisor {index + 1} — {supervisor.name}
                                                                                                            </span>

                                                                                                            <span className="text-muted small">
                                                                                                                {supervisor.agents?.length || 0} agents
                                                                                                            </span>
                                                                                                        </div>

                                                                                                        {/* Supervisor Status */}
                                                                                                        <span className="badge rounded-pill bg-soft-success text-success">
                                                                                                            {supervisor.status}
                                                                                                        </span>

                                                                                                    </div>

                                                                                                    <div className="d-flex align-items-center gap-2">
                                                                                                        {selectedAgentIds.length > 0 &&
                                                                                                            selectedSupervisorId === supervisor.id && (
                                                                                                                <button
                                                                                                                    type="button"
                                                                                                                    className="btn btn-remove bg-soft-danger rounded-3 text-danger border border-danger"
                                                                                                                    onClick={() => handleRemove(
                                                                                                                        selectedCampaign,
                                                                                                                        queue.id,
                                                                                                                        selectedAgentIds
                                                                                                                    )}
                                                                                                                >
                                                                                                                    Remove Selected ({selectedAgentIds.length})
                                                                                                                </button>
                                                                                                            )}
                                                                                                        {/* Remove Supervisor */}
                                                                                                        <button
                                                                                                            type="button"
                                                                                                            className="btn btn-remove bg-soft-danger rounded-3 text-danger border border-danger"
                                                                                                            onClick={() =>
                                                                                                                handleRemove(
                                                                                                                    selectedCampaign,
                                                                                                                    queue.id,
                                                                                                                    [],
                                                                                                                    supervisor.id
                                                                                                                )
                                                                                                            }
                                                                                                        >
                                                                                                            Remove Supervisor
                                                                                                        </button>
                                                                                                    </div>
                                                                                                </div>
                                                                                            </div>

                                                                                            {supervisor.agents?.length > 0 ? (
                                                                                                <div
                                                                                                    style={{
                                                                                                        width: "100%",
                                                                                                        overflowX: "auto"
                                                                                                    }}
                                                                                                >
                                                                                                    <div style={{ minWidth: "700px" }}>

                                                                                                        {/* Table Header */}
                                                                                                        <div
                                                                                                            className="px-3 py-2 border-bottom bg-gray-50"
                                                                                                            style={{
                                                                                                                display: "grid",
                                                                                                                gridTemplateColumns: GRID_COLUMNS,
                                                                                                                alignItems: "center",
                                                                                                            }}
                                                                                                        >
                                                                                                            <div></div>
                                                                                                            <div className="small fw-semibold text-muted">
                                                                                                                AGENT
                                                                                                            </div>

                                                                                                            <div className="small fw-semibold text-muted">
                                                                                                                ROLE
                                                                                                            </div>

                                                                                                            <div className="small fw-semibold text-muted">
                                                                                                                STATUS
                                                                                                            </div>

                                                                                                            <div className="small fw-semibold text-muted">
                                                                                                                REMOVE
                                                                                                            </div>
                                                                                                        </div>


                                                                                                        {/* Agent List */}
                                                                                                        <div
                                                                                                            style={{
                                                                                                                maxHeight: "280px",
                                                                                                                overflowY: "auto",
                                                                                                            }}
                                                                                                        >
                                                                                                            {supervisor.agents?.map((agent) => (
                                                                                                                <div
                                                                                                                    key={agent.id}
                                                                                                                    className="px-3 py-2 border-bottom"
                                                                                                                    style={{
                                                                                                                        display: "grid",
                                                                                                                        gridTemplateColumns: GRID_COLUMNS,
                                                                                                                        alignItems: "center",
                                                                                                                    }}
                                                                                                                >

                                                                                                                    {/* Checkbox */}
                                                                                                                    <div>
                                                                                                                        <input
                                                                                                                            type="checkbox"
                                                                                                                            checked={selectedAgentIds.includes(agent.id)}
                                                                                                                            onChange={() => handleAgentSelect(agent.id, supervisor.id)}
                                                                                                                        />
                                                                                                                    </div>


                                                                                                                    {/* Agent */}
                                                                                                                    <div className="d-flex flex-column">
                                                                                                                        <span className="fw-semibold text-dark">
                                                                                                                            {agent.name}
                                                                                                                        </span>

                                                                                                                        <span className="text-muted small">
                                                                                                                            {agent.flexz_id}
                                                                                                                        </span>
                                                                                                                    </div>


                                                                                                                    {/* Role */}
                                                                                                                    <div>
                                                                                                                        <span
                                                                                                                            className='badge rounded-pill fw-medium bg-soft-teal text-teal'
                                                                                                                        >
                                                                                                                            ● {agent.role}
                                                                                                                        </span>
                                                                                                                    </div>


                                                                                                                    {/* Status */}
                                                                                                                    <div>
                                                                                                                        <span
                                                                                                                            className={`badge rounded-pill fw-medium ${agent.employee_status === "Active"
                                                                                                                                ? "bg-soft-success text-success"
                                                                                                                                : "bg-gray-100 text-secondary"
                                                                                                                                }`} style={{ width: "70px" }}
                                                                                                                        >
                                                                                                                            ● {agent.employee_status}
                                                                                                                        </span>
                                                                                                                    </div>


                                                                                                                    {/* Remove */}
                                                                                                                    <div>
                                                                                                                        <button
                                                                                                                            type="button"
                                                                                                                            className="btn btn-remove rounded-3 fw-semibold text-danger bg-soft-danger"
                                                                                                                            onClick={() => handleRemove(
                                                                                                                                selectedCampaign,
                                                                                                                                queue.id,
                                                                                                                                [agent.id]
                                                                                                                            )}
                                                                                                                        >
                                                                                                                            Remove
                                                                                                                        </button>
                                                                                                                    </div>

                                                                                                                </div>
                                                                                                            ))}
                                                                                                        </div>
                                                                                                    </div>
                                                                                                </div>

                                                                                            ) : (

                                                                                                /* No Agents */
                                                                                                <div className="d-flex flex-column align-items-center justify-content-center text-center py-5 flex-grow-1">

                                                                                                    <FiUsers className="text-muted fs-3 mb-2" />
                                                                                                    <span className="fw-semibold text-dark">
                                                                                                        No agents assigned
                                                                                                    </span>

                                                                                                    <span className="text-muted small mt-1">
                                                                                                        There are no agents assigned to this campaign.
                                                                                                    </span>

                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    ))

                                                                                ) : (

                                                                                    <div className="text-center py-5">

                                                                                        <FiUserCheck className='text-muted fs-3' />

                                                                                        <div className="fw-semibold text-dark mt-2">
                                                                                            No supervisors assigned
                                                                                        </div>

                                                                                        <div className="text-muted small">
                                                                                            There are no supervisors assigned to this queue.
                                                                                        </div>

                                                                                    </div>

                                                                                )}
                                                                            </>
                                                                        )}

                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}

                                                </div>
                                            ) : (
                                                /* No Queues */
                                                <div className="d-flex flex-column align-items-center justify-content-center text-center py-5 flex-grow-1">

                                                    <MdOutlineAccountTree className='text-muted fs-3 mb-2' />

                                                    <span className="fw-semibold text-dark">
                                                        No queues assigned
                                                    </span>

                                                    <span className="text-muted small mt-1">
                                                        There are no queues assigned to this campaign.
                                                    </span>

                                                </div>

                                            )

                                        ) : (
                                            /* No Campaign */
                                            <div className="d-flex flex-column align-items-center justify-content-center text-center py-5 flex-grow-1">

                                                <BsMegaphone className="text-muted fs-3 mb-2" />

                                                <span className="fw-semibold text-dark">
                                                    Select a campaign
                                                </span>

                                                <span className="text-muted small mt-1">
                                                    Select a campaign to manage its agents.
                                                </span>

                                            </div>

                                        )}

                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <AddAgentModal
                        show={showAddAgentModal}
                        onClose={() => setShowAddAgentModal(false)}
                        onSave={async () => {
                            await fetchAgents(selectedCampaign, openQueueId);
                            await refreshCampaignData(selectedCampaign);
                            setShowAddAgentModal(false);
                        }}
                        campaignId={selectedCampaign}
                        queueId={openQueueId}
                    />
                </>
            }
        </div>
    )
}
export default AgentAssignment